import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Avatar,
  Badge,
  Drawer,
  useMediaQuery,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  AppBar,
  Toolbar,
  Button,
} from '@mui/material';
import {
  NotificationsIcon,
  NotificationsOutlinedIcon,
  SettingsIcon,
  SettingsOutlinedIcon,
  CalendarMonthIcon,
  CalendarMonthOutlinedIcon,
  DashboardIcon,
  DashboardOutlinedIcon,
  WorkIcon,
  WorkOutlineIcon,
  PeopleIcon,
  PeopleOutlineIcon,
  SchoolIcon,
  GroupIcon,
  SchoolOutlinedIcon,
  AssignmentIcon,
  AssignmentOutlinedIcon,
  AnalyticsIcon,
  AnalyticsOutlinedIcon,
  LogoutIcon,
  MenuIcon,
  CloseIcon,
  MenuBookIcon,
  MenuBookOutlinedIcon,
  CampaignIcon,
  CampaignOutlinedIcon,
  PersonOutlinedIcon,
  BusinessOutlinedIcon,
  HelpOutlineOutlinedIcon,
  TimelineIcon,
} from '@/components/common/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { useAuth, useAppData } from '@/providers';
import { ROUTES, NAV_MENU_ITEMS, generatePath } from '@/routes/routes';
import { NotificationDialog } from '@/features/notifications/components/NotificationDialog';
import { authService } from '@/services/auth.service';
import { buildTrainingPortalEnterUrl } from '@/utils';
import { useFeatureToggle, FEATURE_FLAGS } from '@/hooks/useFeatureToggle';
import { useUserPermissionsSync } from '@/hooks/useUserPermissionsSync';
import { useCollegeClaims, hasClaimValue } from '@/api/queries/profile.queries';
import { ConfirmationDialog, ShareWhatsAppMessageDialog } from '@/components/common';
import { getWhatsAppShareConfig } from '@/services/firebase.config';
import centrajobLogo from '@/assets/images/CentraJobLogo_New.svg';
import { colors as themeColors, brand, error as errorShades } from '@/theme/colors';
import ChatBot from '@/components/chatbot/ChatBot';

// Items that are allowed when college is pending/inreview
const ALLOWED_ITEMS_WHEN_RESTRICTED = ['dashboard', 'manageCourse'];

// Mapping of nav item keys to their permission names
const NAV_ITEM_PERMISSION_MAP: Record<string, string> = {
  manageApplications: 'enableAccessToTrackCandidateApplications',
};

// Icon mapping for navigation items - Filled icons (for active state)
const iconMapFilled: Record<string, React.ElementType> = {
  dashboard: DashboardIcon,
  work: WorkIcon,
  people: PeopleIcon,
  school: SchoolIcon,
  assignment: AssignmentIcon,
  analytics: AnalyticsIcon,
  batch: PeopleIcon,
  menuBook: MenuBookIcon,
  calendarMonth: CalendarMonthIcon,
  notifications: NotificationsIcon,
  campaign: CampaignIcon,
};

// Icon mapping for navigation items - Outlined icons (for inactive state)
const iconMapOutlined: Record<string, React.ElementType> = {
  dashboard: DashboardOutlinedIcon,
  work: WorkOutlineIcon,
  people: PeopleOutlineIcon,
  school: SchoolOutlinedIcon,
  assignment: AssignmentOutlinedIcon,
  analytics: AnalyticsOutlinedIcon,
  batch: PeopleOutlineIcon,
  menuBook: MenuBookOutlinedIcon,
  calendarMonth: CalendarMonthOutlinedIcon,
  notifications: NotificationsOutlinedIcon,
  campaign: CampaignOutlinedIcon,
};

// Clean minimal color palette using theme colors
const headerColors = {
  background: themeColors.background.paper,
  backgroundLight: themeColors.background.default,
  primary: themeColors.primary.main,
  primaryLight: brand.shade1,
  text: themeColors.text.primary,
  textMuted: themeColors.text.hint,
  border: themeColors.divider,
  hover: themeColors.background.light,
  activeTab: brand.shade7,
  inactiveTab: themeColors.text.secondary,
  error: errorShades.shade7,
};

export const MainLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, collegeProfile } = useAuth();
  const { coordinatorProfile, unreadNotificationCount, markNotificationsAsRead } = useAppData();
  const { isEnabled } = useFeatureToggle();
  const { data: collegeClaims } = useCollegeClaims();

  // Sync user permissions from API on every page load (for non-admin users)
  useUserPermissionsSync();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Check if nursing flow (batch management) is enabled
  const isNursingFlowEnabled = hasClaimValue(collegeClaims, 'enableIsNursingFlow');

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = useState(false);

  // Check if college is in restricted status (pending or inreview)
  const collegeStatus =
    collegeProfile?.status?.toLowerCase() || authService.getStatus()?.toLowerCase() || '';
  const isRestricted = collegeStatus === 'pending' || collegeStatus === 'inreview';

  // Prevent browser back button from navigating to auth pages
  useEffect(() => {
    // Push current state to prevent going back to login page
    const handlePopState = () => {
      // If user tries to go back, push them forward to current page
      window.history.pushState(null, '', window.location.href);
    };

    // Add initial history entry to prevent back navigation
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // WhatsApp share config
  const whatsAppConfig = getWhatsAppShareConfig();
  const isApproved = collegeStatus === 'approved';
  const showWhatsAppButton = isApproved && whatsAppConfig.isEnabled;

  // Filter navigation items based on feature flags and claims
  const visibleNavItems = NAV_MENU_ITEMS.filter((item) => {
    // Hide batch management if nursing flow is not enabled
    if (item.key === 'batchManagement' && !isNursingFlowEnabled) return false;
    // Always show items without feature flags
    if (!('featureFlag' in item) || !item.featureFlag) return true;
    // Check if feature is enabled
    return isEnabled(item.featureFlag as keyof typeof FEATURE_FLAGS);
  });

  // Check if a nav item is allowed based on restriction and permissions
  const isItemAllowed = (itemKey: string): boolean => {
    if (isRestricted && !ALLOWED_ITEMS_WHEN_RESTRICTED.includes(itemKey)) return false;
    const permissionName = NAV_ITEM_PERMISSION_MAP[itemKey];
    if (permissionName && !authService.hasPermission(permissionName)) return false;
    return true;
  };

  // Get tooltip message for disabled nav items
  const getDisabledTooltip = (itemKey: string): string => {
    const permissionName = NAV_ITEM_PERMISSION_MAP[itemKey];
    if (permissionName && !authService.hasPermission(permissionName)) {
      return "You don't have access to track candidate applications";
    }
    if (isRestricted && !ALLOWED_ITEMS_WHEN_RESTRICTED.includes(itemKey)) {
      return 'Access will be enabled after verification is approved';
    }
    return '';
  };

  // Check if current path matches nav item
  const isActive = (path: string): boolean => {
    const getBaseSegment = (p: string) => p.split('/').filter(Boolean)[0] || '';
    const navSegment = getBaseSegment(path);
    const currentSegment = getBaseSegment(location.pathname);

    if (navSegment === currentSegment) return true;

    const relatedPaths: Record<string, string[]> = {
      'manage-jobs': ['job-preview'],
      'manage-applications': ['application-details'],
      'manage-candidates': ['candidate-details'],
      'batches-management': ['create-batch', 'batch-details'],
    };

    if (relatedPaths[navSegment]?.includes(currentSegment)) return true;

    return false;
  };

  const handleNavClick = (path: string) => {
    setMobileDrawerOpen(false);
    // Check if batch management should redirect to training portal
    if (path === ROUTES.batchManagement && isEnabled('enableBatchTrainingPortal')) {
      const token = authService.getAccessToken();
      if (token) {
        window.open(buildTrainingPortalEnterUrl(token), '_blank', 'noopener,noreferrer');
        return;
      }
    }
    navigate(path);
  };

  const handleProfileClick = () => {
    navigate(generatePath(ROUTES.settings, { path: 'personal-info' }));
  };

  const handleNotificationClick = () => {
    setNotificationOpen(true);
  };

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const handleConfirmLogout = async () => {
    setLogoutDialogOpen(false);
    await logout();
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const getInitials = (): string => {
    if (!coordinatorProfile) return 'U';
    const firstName = coordinatorProfile.firstName || '';
    const lastName = coordinatorProfile.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const getFullName = (): string => {
    if (!coordinatorProfile) return 'User';
    return (
      `${coordinatorProfile.firstName || ''} ${coordinatorProfile.lastName || ''}`.trim() || 'User'
    );
  };

  // Mobile Navigation Drawer Content
  const mobileDrawerContent = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        bgcolor: headerColors.background,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 64,
          px: 2,
          borderBottom: `1px solid ${headerColors.border}`,
        }}
      >
        <Box component="img" src={centrajobLogo} alt="CentraJob" sx={{ height: 28 }} />
        <IconButton onClick={toggleMobileDrawer}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* User Info */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${headerColors.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={coordinatorProfile?.profileImage}
            sx={{
              width: 44,
              height: 44,
              bgcolor: headerColors.primary,
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            {getInitials()}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600} color={headerColors.text}>
              {getFullName()}
            </Typography>
            <Typography variant="caption" color={headerColors.textMuted}>
              {coordinatorProfile?.role || ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, py: 2, overflow: 'auto' }}>
        <List sx={{ px: 1 }}>
          {visibleNavItems.map((item) => {
            const active = isActive(item.path);
            const Icon = active
              ? iconMapFilled[item.icon] || DashboardIcon
              : iconMapOutlined[item.icon] || DashboardOutlinedIcon;
            const allowed = isItemAllowed(item.key);

            return (
              <Tooltip
                key={item.key}
                title={!allowed ? getDisabledTooltip(item.key) : ''}
                placement="right"
              >
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => allowed && handleNavClick(item.path)}
                    disabled={!allowed}
                    sx={{
                      minHeight: 48,
                      borderRadius: 2,
                      px: 2,
                      background: active ? alpha(headerColors.primary, 0.08) : 'transparent',
                      borderLeft: active
                        ? `3px solid ${headerColors.primary}`
                        : '3px solid transparent',
                      opacity: allowed ? 1 : 0.5,
                      cursor: allowed ? 'pointer' : 'not-allowed',
                      '&:hover': {
                        background: allowed
                          ? active
                            ? alpha(headerColors.primary, 0.12)
                            : headerColors.hover
                          : 'transparent',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.5,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: allowed
                          ? active
                            ? headerColors.primary
                            : headerColors.text
                          : headerColors.textMuted,
                      }}
                    >
                      <Icon sx={{ fontSize: 22 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: active ? 600 : 400,
                        color: allowed
                          ? active
                            ? headerColors.primary
                            : headerColors.text
                          : headerColors.textMuted,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          })}

          {/* Calendar */}
          {isEnabled(FEATURE_FLAGS.enableEventsCalendar) && (
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => !isRestricted && handleNavClick(ROUTES.eventCalendar)}
                disabled={isRestricted}
                sx={{
                  minHeight: 48,
                  borderRadius: 2,
                  px: 2,
                  background: isActive(ROUTES.eventCalendar)
                    ? alpha(headerColors.primary, 0.08)
                    : 'transparent',
                  borderLeft: isActive(ROUTES.eventCalendar)
                    ? `3px solid ${headerColors.primary}`
                    : '3px solid transparent',
                  opacity: isRestricted ? 0.5 : 1,
                  '&:hover': {
                    background: !isRestricted
                      ? isActive(ROUTES.eventCalendar)
                        ? alpha(headerColors.primary, 0.12)
                        : headerColors.hover
                      : 'transparent',
                  },
                  '&.Mui-disabled': { opacity: 0.5 },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isActive(ROUTES.eventCalendar)
                      ? headerColors.primary
                      : headerColors.text,
                  }}
                >
                  {isActive(ROUTES.eventCalendar) ? (
                    <CalendarMonthIcon sx={{ fontSize: 22 }} />
                  ) : (
                    <CalendarMonthOutlinedIcon sx={{ fontSize: 22 }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="Events Calendar"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: isActive(ROUTES.eventCalendar) ? 600 : 400,
                    color: isActive(ROUTES.eventCalendar)
                      ? headerColors.primary
                      : headerColors.text,
                  }}
                />
              </ListItemButton>
            </ListItem>
          )}

          {/* Settings */}
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() =>
                handleNavClick(generatePath(ROUTES.settings, { path: 'tpo-settings' }))
              }
              sx={{
                minHeight: 48,
                borderRadius: 2,
                px: 2,
                background: isActive(ROUTES.settings)
                  ? alpha(headerColors.primary, 0.08)
                  : 'transparent',
                borderLeft: isActive(ROUTES.settings)
                  ? `3px solid ${headerColors.primary}`
                  : '3px solid transparent',
                '&:hover': {
                  background: isActive(ROUTES.settings)
                    ? alpha(headerColors.primary, 0.12)
                    : headerColors.hover,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: isActive(ROUTES.settings) ? headerColors.primary : headerColors.text,
                }}
              >
                {isActive(ROUTES.settings) ? (
                  <SettingsIcon sx={{ fontSize: 22 }} />
                ) : (
                  <SettingsOutlinedIcon sx={{ fontSize: 22 }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Settings"
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: isActive(ROUTES.settings) ? 600 : 400,
                  color: isActive(ROUTES.settings) ? headerColors.primary : headerColors.text,
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      {/* Logout */}
      <Box sx={{ p: 2, borderTop: `1px solid ${headerColors.border}` }}>
        <ListItemButton
          onClick={() => {
            setMobileDrawerOpen(false);
            handleLogout();
          }}
          sx={{
            borderRadius: 2,
            color: headerColors.error,
            '&:hover': { bgcolor: alpha(headerColors.error, 0.1) },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: headerColors.error }}>
            <LogoutIcon sx={{ fontSize: 22 }} />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  // Mobile Layout
  if (isMobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: headerColors.backgroundLight,
        }}
      >
        {/* Mobile App Bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: headerColors.background,
            borderBottom: `1px solid ${headerColors.border}`,
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                edge="start"
                onClick={toggleMobileDrawer}
                sx={{ color: headerColors.text }}
              >
                <MenuIcon />
              </IconButton>
              <Box
                component="img"
                src="/favicon.svg"
                alt="CentraJob"
                onClick={() => navigate(ROUTES.dashboard)}
                sx={{ height: 28, width: 28, cursor: 'pointer' }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* WhatsApp Share - Mobile */}
              {showWhatsAppButton && (
                <Box
                  component="img"
                  src="/whatsapp-logo.png"
                  alt="WhatsApp"
                  onClick={() => setWhatsAppDialogOpen(true)}
                  sx={{
                    width: 24,
                    height: 24,
                    cursor: 'pointer',
                  }}
                />
              )}
              {isEnabled(FEATURE_FLAGS.enableCollegeNotifications) && (
                <IconButton
                  onClick={handleNotificationClick}
                  sx={{
                    color: headerColors.textMuted,
                  }}
                  size="small"
                >
                  <Badge
                    badgeContent={unreadNotificationCount}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: 9,
                        minWidth: 16,
                        height: 16,
                        padding: '0 4px',
                      },
                    }}
                  >
                    <NotificationsOutlinedIcon fontSize="small" />
                  </Badge>
                </IconButton>
              )}
              <Avatar
                src={coordinatorProfile?.profileImage}
                onClick={handleProfileClick}
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: headerColors.primary,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  ml: 0.5,
                }}
              >
                {getInitials()}
              </Avatar>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={toggleMobileDrawer}
          PaperProps={{
            sx: { width: 280 },
          }}
        >
          {mobileDrawerContent}
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Outlet />
        </Box>

        {/* Notification Dialog */}
        <NotificationDialog
          open={notificationOpen}
          onClose={() => setNotificationOpen(false)}
          onMarkAsRead={markNotificationsAsRead}
        />

        {/* WhatsApp Share Dialog */}
        <ShareWhatsAppMessageDialog
          open={whatsAppDialogOpen}
          onClose={() => setWhatsAppDialogOpen(false)}
        />

        {/* Logout Confirmation Dialog */}
        <ConfirmationDialog
          open={logoutDialogOpen}
          onClose={() => setLogoutDialogOpen(false)}
          onConfirm={handleConfirmLogout}
          title="Logout"
          message="Are you sure you want to logout?"
          confirmText="Logout"
          cancelText="Cancel"
        />

        {/* Chatbot */}
        {isEnabled(FEATURE_FLAGS.enableChatbot) && <ChatBot />}
      </Box>
    );
  }

  // Desktop Layout - Horizontal Tabs Navigation
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: headerColors.backgroundLight,
      }}
    >
      {/* Top Header Bar with Horizontal Navigation */}
      <Box
        sx={{
          bgcolor: headerColors.background,
          borderBottom: `1px solid ${headerColors.border}`,
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            height: 64,
          }}
        >
          {/* Left Side - Logo and Navigation Tabs */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 }, height: '100%' }}
          >
            {/* Logo */}
            <Box
              component="img"
              src={centrajobLogo}
              alt="CentraJob"
              onClick={() => navigate(ROUTES.dashboard)}
              sx={{
                height: 32,
                cursor: 'pointer',
              }}
            />

            {/* Navigation Tabs */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0, md: 0.5 },
                height: '100%',
              }}
            >
              {visibleNavItems.map((item) => {
                const active = isActive(item.path);
                const allowed = isItemAllowed(item.key);

                return (
                  <Tooltip
                    key={item.key}
                    title={!allowed ? getDisabledTooltip(item.key) : ''}
                    placement="bottom"
                  >
                    <span style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                      <Button
                        onClick={() => allowed && handleNavClick(item.path)}
                        disabled={!allowed}
                        disableRipple
                        sx={{
                          px: { xs: 1.5, md: 2 },
                          py: 1,
                          borderRadius: 1,
                          textTransform: 'none',
                          fontSize: { xs: 13, md: 14 },
                          fontWeight: active ? 600 : 500,
                          color: allowed
                            ? active
                              ? headerColors.primary
                              : headerColors.inactiveTab
                            : headerColors.textMuted,
                          position: 'relative',
                          minWidth: 'auto',
                          height: '100%',
                          '&:hover': {
                            bgcolor: allowed
                              ? active
                                ? alpha(headerColors.primary, 0.12)
                                : headerColors.hover
                              : 'transparent',
                          },
                          '&:focus': {
                            bgcolor: 'transparent',
                          },
                          '&:focus-visible': {
                            outline: 'none',
                            bgcolor: 'transparent',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: 3,
                              bgcolor: headerColors.primary,
                              borderRadius: '3px 3px 0 0',
                            },
                          },
                          '&.Mui-focusVisible': {
                            bgcolor: 'transparent',
                          },
                          '&.Mui-disabled': {
                            color: headerColors.textMuted,
                            opacity: 0.5,
                          },
                          '&::after': active
                            ? {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: 3,
                                bgcolor: headerColors.primary,
                                borderRadius: '3px 3px 0 0',
                              }
                            : {},
                        }}
                      >
                        {item.title}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>

          {/* Right Side - Actions and Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* WhatsApp Share */}
            {showWhatsAppButton && (
              <Tooltip title="Share App with Students" arrow placement="bottom">
                <Box
                  component="img"
                  src="/whatsapp-logo.png"
                  alt="WhatsApp"
                  onClick={() => setWhatsAppDialogOpen(true)}
                  sx={{
                    width: 25,
                    height: 25,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease',
                    '&:hover': { transform: 'scale(1.1)' },
                  }}
                />
              </Tooltip>
            )}

            {/* Notifications */}
            {isEnabled(FEATURE_FLAGS.enableCollegeNotifications) && (
              <Tooltip title="Notifications">
                <span>
                  <IconButton
                    onClick={handleNotificationClick}
                    sx={{
                      color: headerColors.textMuted,
                      '&:hover': {
                        color: headerColors.text,
                        bgcolor: headerColors.hover,
                      },
                      '&:focus-visible': {
                        outline: `2px solid ${headerColors.primary}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Badge
                      badgeContent={unreadNotificationCount}
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          bgcolor: headerColors.error,
                          color: 'white',
                          fontSize: 10,
                          minWidth: 18,
                          height: 18,
                        },
                      }}
                    >
                      <NotificationsOutlinedIcon />
                    </Badge>
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Events Calendar */}
            {isEnabled(FEATURE_FLAGS.enableEventsCalendar) && (
              <Tooltip
                title={
                  isRestricted
                    ? 'Access will be enabled after verification is approved'
                    : 'Events Calendar'
                }
                placement="bottom"
              >
                <span>
                  <IconButton
                    onClick={() => !isRestricted && navigate(ROUTES.eventCalendar)}
                    disabled={isRestricted}
                    sx={{
                      color: isActive(ROUTES.eventCalendar)
                        ? headerColors.primary
                        : headerColors.textMuted,
                      '&:hover': { color: headerColors.text, bgcolor: headerColors.hover },
                      '&:focus-visible': {
                        outline: `2px solid ${headerColors.primary}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    {isActive(ROUTES.eventCalendar) ? (
                      <CalendarMonthIcon />
                    ) : (
                      <CalendarMonthOutlinedIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {/* Settings */}
            <Tooltip title="Settings">
              <IconButton
                onClick={() => navigate(generatePath(ROUTES.settings, { path: 'tpo-settings' }))}
                sx={{
                  color: headerColors.textMuted,
                  '&:hover': { color: headerColors.text, bgcolor: headerColors.hover },
                  '&:focus-visible': {
                    outline: `2px solid ${headerColors.primary}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <SettingsOutlinedIcon />
              </IconButton>
            </Tooltip>

            {/* Hamburger Menu */}
            <Tooltip title="Menu">
              <IconButton
                onClick={toggleMobileDrawer}
                sx={{
                  color: headerColors.textMuted,
                  '&:hover': { color: headerColors.text, bgcolor: headerColors.hover },
                  '&:focus-visible': {
                    outline: `2px solid ${headerColors.primary}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>

            {/* User Profile Avatar */}
            <Avatar
              src={coordinatorProfile?.profileImage}
              onClick={handleProfileClick}
              tabIndex={0}
              sx={{
                width: 40,
                height: 40,
                bgcolor: headerColors.primary,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                ml: 0.5,
                '&:hover': {
                  boxShadow: `0 0 0 2px ${alpha(headerColors.primary, 0.3)}`,
                },
                '&:focus-visible': {
                  outline: `2px solid ${headerColors.primary}`,
                  outlineOffset: 2,
                },
              }}
            >
              {getInitials()}
            </Avatar>
          </Box>
        </Box>
      </Box>

      {/* Side Menu Drawer (for additional options) */}
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={toggleMobileDrawer}
        PaperProps={{
          sx: {
            width: 320,
            borderRadius: '16px 0 0 16px',
          },
        }}
      >
        <Box
          sx={{
            width: 320,
            height: '100%',
            bgcolor: headerColors.background,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* User Profile Section */}
          <Box
            sx={{
              pt: 3,
              px: 3,
              pb: 3,
              borderBottom: `1px solid ${headerColors.border}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar
                src={coordinatorProfile?.profileImage}
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: headerColors.primary,
                  fontSize: 20,
                  fontWeight: 600,
                }}
              >
                {getInitials()}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  color={headerColors.text}
                  sx={{ mb: 0.25 }}
                >
                  {getFullName()}
                </Typography>
                <Typography
                  variant="caption"
                  color={headerColors.textMuted}
                  sx={{ display: 'block' }}
                >
                  {coordinatorProfile?.coordinatorEmail || ''}
                </Typography>
              </Box>
            </Box>
            <Typography
              variant="body2"
              onClick={() => {
                handleProfileClick();
                toggleMobileDrawer();
              }}
              sx={{
                color: headerColors.primary,
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              View and Update Profile
            </Typography>
          </Box>

          {/* Menu Items */}
          <Box sx={{ flex: 1, py: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>
            <List sx={{ px: 1.5 }}>
              {/* My Profile */}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => {
                    navigate(generatePath(ROUTES.settings, { path: 'personal-info' }));
                    toggleMobileDrawer();
                  }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    '&:hover': { bgcolor: headerColors.hover },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: headerColors.textMuted }}>
                    <PersonOutlinedIcon sx={{ fontSize: 22 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="My Profile"
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: headerColors.text,
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {/* College Profile */}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => {
                    navigate(generatePath(ROUTES.settings, { path: 'college-details' }));
                    toggleMobileDrawer();
                  }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    '&:hover': { bgcolor: headerColors.hover },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: headerColors.textMuted }}>
                    <BusinessOutlinedIcon sx={{ fontSize: 22 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="College/Institution Profile"
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: headerColors.text,
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {/* TPO Settings */}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => {
                    navigate(generatePath(ROUTES.settings, { path: 'tpo-settings' }));
                    toggleMobileDrawer();
                  }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    '&:hover': { bgcolor: headerColors.hover },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: headerColors.textMuted }}>
                    <SettingsOutlinedIcon sx={{ fontSize: 22 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="TPO Settings"
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: headerColors.text,
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {/* Team Management */}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => {
                    navigate(generatePath(ROUTES.settings, { path: 'team' }));
                    toggleMobileDrawer();
                  }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    '&:hover': { bgcolor: headerColors.hover },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: headerColors.textMuted }}>
                    <GroupIcon sx={{ fontSize: 22 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Team Management"
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: headerColors.text,
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {/* Manage Stages */}
              {isEnabled('enableV2ProfileSettingsManageStageTracking') && (
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      navigate(generatePath(ROUTES.settings, { path: 'stages' }));
                      toggleMobileDrawer();
                    }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      '&:hover': { bgcolor: headerColors.hover },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: headerColors.textMuted }}>
                      <TimelineIcon sx={{ fontSize: 22 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Manage Stages"
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: headerColors.text,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {/* Notification */}
              {isEnabled(FEATURE_FLAGS.enableCollegeNotifications) && (
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      handleNotificationClick();
                      toggleMobileDrawer();
                    }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      '&:hover': { bgcolor: headerColors.hover },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: headerColors.textMuted }}>
                      <NotificationsOutlinedIcon sx={{ fontSize: 22 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Notification"
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: headerColors.text,
                      }}
                    />
                    {unreadNotificationCount > 0 && (
                      <Box
                        sx={{
                          bgcolor: headerColors.error,
                          color: 'white',
                          fontSize: 11,
                          fontWeight: 600,
                          minWidth: 20,
                          height: 20,
                          borderRadius: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 0.5,
                        }}
                      >
                        {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
              )}

              {/* FAQ / Help & Support */}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => {
                    navigate(generatePath(ROUTES.settings, { path: 'help' }));
                    toggleMobileDrawer();
                  }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    '&:hover': { bgcolor: headerColors.hover },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: headerColors.textMuted }}>
                    <HelpOutlineOutlinedIcon sx={{ fontSize: 22 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="FAQ"
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: headerColors.text,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>

          {/* Logout */}
          <Box sx={{ p: 2, borderTop: `1px solid ${headerColors.border}` }}>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                '&:hover': { bgcolor: alpha(headerColors.error, 0.08) },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: headerColors.error }}>
                <LogoutIcon sx={{ fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: headerColors.error,
                }}
              />
            </ListItemButton>
          </Box>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        <Outlet />
      </Box>

      {/* Notification Dialog */}
      <NotificationDialog
        open={notificationOpen}
        onClose={() => setNotificationOpen(false)}
        onMarkAsRead={markNotificationsAsRead}
      />

      {/* Logout Confirmation Dialog */}
      <ConfirmationDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleConfirmLogout}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
      />

      {/* WhatsApp Share Dialog */}
      <ShareWhatsAppMessageDialog
        open={whatsAppDialogOpen}
        onClose={() => setWhatsAppDialogOpen(false)}
      />

      {/* Chatbot */}
      {isEnabled(FEATURE_FLAGS.enableChatbot) && <ChatBot />}
    </Box>
  );
};

export default MainLayout;
